import { useEffect, useState } from 'react';
import { notification } from 'antd';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';
import { AnswerTypeEnum, TestFileType } from '../constants/enums';
import { useTranslateTestgroup } from './useTranslateTestgroup';
import useCreateScreening from './useCreateScreening';
import { t } from '../i18n/i18n';
import { getAIQuestionTrial, getAITestTranslation } from '../API/services/screeningService';
import { setTranslateTest } from '../redux/actions/testGroupCreatorActions';

export const useTranslateTest = () => {
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [generatingSuggestion, setGeneratinSuggestion] = useState(false);
  const [loadingAIAnswer, setLoadingAIAnswer] = useState(false);
  const dispatch = useDispatch();
  const maxBytesFile = 1000000;

  const emptyHandler = () => {};
  const {
    testToTranslate,
    originalLanguage,
    languageToTranslate,
    saveTestTranslation,
    updateTranslatedTests,
  } = useTranslateTestgroup();
  const {
    testAnswer,
    setTestAnswer,
    aiImg,
    currentTypeQuestion,
    promptsCorrectionTextAI,
    defaultCheckState,
    fileTypes,
    defaultCodeOutputState,
    handleCurrentTypeQuestion,
    handleAddPrompTextAI,
    handleAddPromptChallengeAI,
    determineAudioDuration,
  } = useCreateScreening();

  useEffect(() => {
    if (testToTranslate) {
      dispatch(setTranslateTest(testToTranslate));
      // setTest(testToTranslate);
    }
  }, [testToTranslate]);

  const uploadTranslateFile = (file, fileType) => {
    if (file.size > maxBytesFile) {
      notification.open({
        message: t('UPLOAD_FILE_ERROR'),
        type: 'error',
      });
    } else {
      const formattedFile = {
        ...file,
        id: v4(),
        fileType,
      };
      return formattedFile;
    }
  };

  const handleUploadFile = (file, fileType) => {
    const fileUploaded = uploadTranslateFile(file, fileType);
    if (!fileUploaded) return;
    const isAudioFile = fileType === TestFileType.AUDIO;
    const testUpdated = {
      ...testToTranslate,
      testFiles: testToTranslate?.testFiles
        ? testToTranslate?.testFiles?.concat(fileUploaded)
        : [fileUploaded],
      listeningTest: testToTranslate?.listeningTest || isAudioFile,
    };

    dispatch(setTranslateTest(testUpdated));
  };

  const handleUpdateFile = (fileId, fileToUpdate) => {
    const testFiles =
      testToTranslate?.testFiles?.map((file) =>
        file.id === fileId
          ? {
              ...file,
              ...fileToUpdate,
              ...(file.duration || fileToUpdate.duration
                ? { audioDuration: determineAudioDuration({ ...file, ...fileToUpdate }) }
                : {}),
            }
          : file
      ) ?? [];

    dispatch(
      setTranslateTest({
        ...testToTranslate,
        testFiles,
      })
    );
  };

  const handleUploadAnswerFile = (optionIndex, file, fileType) => {
    const testToUpdate = { ...testToTranslate };
    const fileUploaded = uploadTranslateFile(file, fileType);
    if (!fileUploaded) return;
    if (testToUpdate.options[optionIndex]?.files?.length) {
      testToUpdate.options[optionIndex]?.files.push(fileUploaded);
    } else {
      testToUpdate.options[optionIndex].files = [fileUploaded];
    }
    dispatch(setTranslateTest(testToUpdate));
  };

  const handleRemoveAnwserTestFile = (optionIndex, fileId) => {
    const testToUpdate = { ...testToTranslate };
    let filesFiltered = null;
    if (testToUpdate?.options?.[optionIndex]?.files?.length > 0) {
      filesFiltered = testToUpdate?.options?.[optionIndex]?.files?.filter((f) => f.id !== fileId);
    }
    testToUpdate.options[optionIndex].files = filesFiltered;
    dispatch(setTranslateTest(testToUpdate));
  };

  const handleUpdateAnswerTestFile = (optionIndex, fileId, fileToUpdate) => {
    const testToUpdate = { ...testToTranslate };
    let filesFiltered = null;
    if (testToUpdate?.options?.[optionIndex]?.files?.length > 0) {
      filesFiltered = testToUpdate?.options?.[optionIndex]?.files?.map((f) =>
        f.id !== fileId ? f : { ...f, fileToUpdate }
      );
    }
    testToUpdate.options[optionIndex].files = filesFiltered;
    dispatch(setTranslateTest(testToUpdate));
  };

  const handleSaveTranslation = async () => {
    try {
      setSavingTranslation(true);
      await saveTestTranslation(testToTranslate);
      notification.open({
        message: t('TEST_TRANSLATION_SAVE_SUCCESSFULL'),
        type: 'success',
      });
      updateTranslatedTests(testToTranslate);
    } catch (e) {
      console.log(e);
      notification.open({
        message: t('TEST_TRANSLATION_SAVE_ERROR'),
        type: 'error',
      });
    } finally {
      setSavingTranslation(false);
    }
  };

  const handleGenerateSuggestion = async () => {
    try {
      setGeneratinSuggestion(true);
      const dataToTranslate = {
        ...testToTranslate,
        testId: testToTranslate.id,
        language: languageToTranslate,
        originalLanguage,
      };
      const suggestion = await getAITestTranslation(dataToTranslate);
      notification.open({
        message: t('TEST_SUGGEST_TRANSLATION_GENERATED'),
        type: 'success',
      });
      dispatch(setTranslateTest({ ...testToTranslate, ...suggestion }));
    } catch (e) {
      notification.open({
        message: t('TEST_SUGGEST_TRANSLATION_ERROR'),
        type: 'error',
      });
    } finally {
      setGeneratinSuggestion(false);
    }
  };

  const handleUpdateTranslatedTest = (key, value) => {
    if (key && key.target) {
      value = key.target?.value;
      key = key.target?.name;
    }
    const testUpdated = {
      ...testToTranslate,
      [key]: value,
    };
    switch (key) {
      case 'file': {
        const filtered = testToTranslate?.testFiles?.filter((file) => file.id !== value) ?? [];
        const listeningTest = filtered.some((file) => file.fileType === TestFileType.AUDIO);
        testUpdated.testFiles = filtered;
        testUpdated.listeningTest = listeningTest;
        break;
      }
      default: {
        testUpdated[key] = value;
        break;
      }
    }
    dispatch(setTranslateTest(testUpdated));
  };

  const handleGenerateAITranslateAnswer = async (answer) => {
    setLoadingAIAnswer(true);
    try {
      const response = await getAIQuestionTrial({
        question: testToTranslate?.text,
        concepts: testToTranslate?.concepts,
        promptSubType: testToTranslate?.promptSubType ?? AnswerTypeEnum.TEXT_AI,
        answer,
      });
      return response;
    } catch (e) {
      notification.open({
        message: t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_ERROR'),
        type: 'error',
      });
      return {};
    } finally {
      setLoadingAIAnswer(false);
    }
  };

  return {
    testToTranslate,
    savingTranslation,
    generatingSuggestion,
    loadingAIAnswer,
    handleGenerateAITranslateAnswer,
    emptyHandler,
    handleUploadFile,
    handleUpdateFile,
    handleUploadAnswerFile,
    handleRemoveAnwserTestFile,
    handleUpdateAnswerTestFile,
    handleSaveTranslation,
    handleGenerateSuggestion,
    handleUpdateTranslatedTest,
    // this is to be used in each question handles QuestionComponentSelected
    currentQuestionData: testToTranslate,
    testAnswer,
    setTestAnswer,
    aiImg,
    currentTypeQuestion,
    promptsCorrectionTextAI,
    defaultCheckState,
    fileTypes,
    defaultCodeOutputState,
    handleAddQuestionText: handleUpdateTranslatedTest,
    handleAddQuestionWrite: handleUpdateTranslatedTest,
    handleAddQuestionChallengeAsync: handleUpdateTranslatedTest,
    handleAddTestFile: handleUploadFile,
    handleUpdateTestFile: handleUpdateFile,
    handleRemoveTestFile: (fileId) => handleUpdateTranslatedTest('file', fileId),
    handleToggleIsTyping: emptyHandler,
    handleGenerateAIAnswer: handleGenerateAITranslateAnswer,
    handleCurrentTypeQuestion,
    handleAddPrompTextAI,
    handleAddPromptChallengeAI,
    handleAddAnswers: (options) => {
      const testUpdated = {
        ...testToTranslate,
        options,
      };
      dispatch(setTranslateTest(testUpdated));
    },
    resetCheckWeight: emptyHandler,
    handleAddAnswerFile: handleUploadAnswerFile,
    handleRemoveAnswerFile: handleRemoveAnwserTestFile,
    handleUpdateAnswerFile: handleUpdateAnswerTestFile,
    handleAddFileTypes: emptyHandler,
    handleAddCodeAnswers: emptyHandler,
    handleAddQuestionLanguage: emptyHandler,
  };
};
